import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Button, Label } from '@primer/react';
import type { Proposal, ProposalDraft, ProposalKind, ProposalStatus } from '@autonomous-team/domain';

import { formatTimestamp } from '../../logic/format.js';
import { useConsoleMutation, useProposals } from '../queries.js';
import { QueryPhase } from '../components/state.js';
import { TeamChangeProposalReview } from '../components/team-change-proposal.js';

type KindFilter = ProposalKind | 'all';
type StatusFilter = ProposalStatus | 'all';

export function ProposalsScreen(): ReactNode {
  const [kind, setKind] = useState<KindFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('proposed');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const filter = useMemo(() => ({
    ...(kind === 'all' ? {} : { kind }),
    ...(status === 'all' ? {} : { status }),
  }), [kind, status]);
  const proposals = useProposals(filter);
  const items = proposals.data ?? [];

  useEffect(() => {
    if (items.length === 0) setSelectedId(null);
    else if (!items.some((item) => item.id === selectedId)) setSelectedId(items[0]?.id ?? null);
  }, [items, selectedId]);

  const selected = items.find((item) => item.id === selectedId) ?? null;
  const blocking = proposals.isPending || proposals.error !== null || items.length === 0;

  return (
    <div className="proposals-screen">
      <header className="proposals-toolbar">
        <div>
          <h2>Agent proposals</h2>
          <p>Suggestions never start work by themselves. Review the exact draft, then approve or reject it.</p>
        </div>
        <div className="proposals-filters" aria-label="Proposal filters">
          <label>Type<select value={kind} onChange={(event) => setKind(event.target.value as KindFilter)}><option value="all">All</option><option value="task">Tasks</option><option value="skill">Skills</option><option value="team-change">Team changes</option></select></label>
          <label>Status<select value={status} onChange={(event) => setStatus(event.target.value as StatusFilter)}><option value="proposed">Waiting</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="all">All</option></select></label>
        </div>
      </header>

      <div className="proposals-workspace">
        <aside className="proposals-list" aria-label="Proposals">
          {blocking ? (
            <QueryPhase isPending={proposals.isPending} error={proposals.error} isEmpty={items.length === 0} source="proposals" emptyTitle="No matching proposals" emptyDetail="Try another type or status." onRetry={() => void proposals.refetch()} rows={4} />
          ) : items.map((proposal) => (
            <button className={`proposal-row${proposal.id === selectedId ? ' selected' : ''}`} key={proposal.id} onClick={() => setSelectedId(proposal.id)} type="button">
              <span className="proposal-row-meta"><Label variant={proposal.kind === 'skill' ? 'accent' : 'secondary'}>{proposal.kind}</Label><span>{proposal.status}</span></span>
              <strong>{proposal.draft.title}</strong>
              <span>{proposal.draft.benefit}</span>
              <small>{formatTimestamp(proposal.updatedAt)}</small>
            </button>
          ))}
        </aside>
        <main className="proposal-detail">{selected === null ? null : <ProposalEditor proposal={selected} />}</main>
      </div>
    </div>
  );
}

function ProposalEditor({ proposal }: { readonly proposal: Proposal }): ReactNode {
  const [draft, setDraft] = useState<ProposalDraft>(proposal.draft);
  const [note, setNote] = useState('');
  useEffect(() => { setDraft(proposal.draft); setNote(''); }, [proposal]);

  const edit = useConsoleMutation<{ draft: ProposalDraft; revision: number }, Proposal>(
    (data, context, args) => data.editProposal(proposal.id, args.revision, args.draft, context),
    [['proposals']],
  );
  const decide = useConsoleMutation<{ decision: 'approve' | 'reject'; revision: number; note: string }, Proposal>(
    (data, context, args) => args.decision === 'approve'
      ? data.approveProposal(proposal.id, args.revision, args.note, context)
      : data.rejectProposal(proposal.id, args.revision, args.note, context),
    [['proposals'], ['board'], ['human-reviews']],
  );
  const changed = JSON.stringify(draft) !== JSON.stringify(proposal.draft);
  const pending = edit.isPending || decide.isPending;

  if (proposal.kind === 'team-change') {
    return <div className="proposal-editor"><TeamChangeProposalReview proposal={proposal} /></div>;
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    edit.mutate({ draft, revision: proposal.revision });
  }
  const field = (key: keyof ProposalDraft, value: string | readonly string[]) => setDraft((current) => ({ ...current, [key]: value }));

  return (
    <form className="proposal-editor" onSubmit={submit}>
      <header>
        <div><Label variant={proposal.kind === 'skill' ? 'accent' : 'secondary'}>{proposal.kind}</Label><span className="proposal-status">{proposal.status}</span></div>
        <h2>{proposal.draft.title}</h2>
        <p>Proposed by {proposal.authorId} · revision {proposal.revision} · {formatTimestamp(proposal.createdAt)}</p>
      </header>

      <div className="proposal-form-grid">
        <label>Title<input value={draft.title} disabled={proposal.status !== 'proposed'} onChange={(event) => field('title', event.target.value)} /></label>
        <label>Benefit<input value={draft.benefit} disabled={proposal.status !== 'proposed'} onChange={(event) => field('benefit', event.target.value)} /></label>
        <label className="wide">Why this is needed<textarea value={draft.rationale} disabled={proposal.status !== 'proposed'} onChange={(event) => field('rationale', event.target.value)} /></label>
        <label className="wide">Proposed scope {proposal.kind === 'skill' ? '/ SKILL.md' : ''}<textarea className="proposal-body" value={draft.body} disabled={proposal.status !== 'proposed'} onChange={(event) => field('body', event.target.value)} /></label>
        <label>Acceptance criteria <span className="hint">one per line</span><textarea value={draft.acceptanceCriteria.join('\n')} disabled={proposal.status !== 'proposed'} onChange={(event) => field('acceptanceCriteria', lines(event.target.value))} /></label>
        <label>Repositories <span className="hint">one per line</span><textarea value={draft.repositories.join('\n')} disabled={proposal.status !== 'proposed'} onChange={(event) => field('repositories', lines(event.target.value))} /></label>
      </div>

      {proposal.status === 'proposed' ? <div className="proposal-actions">
        <Button type="submit" disabled={!changed || pending}>{edit.isPending ? 'Saving…' : 'Save draft'}</Button>
        <label className="proposal-note">Decision note <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional context for the audit trail" /></label>
        <Button variant="danger" type="button" disabled={pending || changed} onClick={() => decide.mutate({ decision: 'reject', revision: proposal.revision, note })}>Reject</Button>
        <Button variant="primary" type="button" disabled={pending || changed} onClick={() => decide.mutate({ decision: 'approve', revision: proposal.revision, note })}>{decide.isPending ? 'Recording…' : proposal.kind === 'skill' ? 'Approve human task' : 'Approve to Backlog'}</Button>
      </div> : <p className="proposal-decision">Decided by {proposal.decisionActorId ?? 'unknown'}{proposal.decisionNote ? ` — ${proposal.decisionNote}` : ''}</p>}
      {edit.error === null && decide.error === null ? null : <p className="error-banner">{(edit.error ?? decide.error)?.message}</p>}
    </form>
  );
}

function lines(value: string): readonly string[] {
  return value.split('\n').map((line) => line.trim()).filter(Boolean);
}
